import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { StorageKeys } from '../../services/wrapper/storage-keys';
import { AfRoutes } from '../../routes';
import { LogoutState, LogoutStatus } from './logout-state';

@Component({
  selector: 'af-logout',
  imports: [ButtonsModule, LoaderModule],
  templateUrl: './logout.component.html',
  styleUrl: './logout.component.scss',
})
export class LogoutComponent implements OnInit {
  public readonly LogoutStatus = LogoutStatus;
  public status: LogoutStatus = LogoutStatus.PENDING;
  public idpLogoutUrl?: string;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    const storedState = this.getStoredLogoutState();

    this.status = storedState?.status ?? LogoutStatus.COMPLETED;
    this.idpLogoutUrl = storedState?.idpLogoutUrl ?? undefined;
  }

  public async logoutFromIdp(): Promise<void> {
    if (!this.idpLogoutUrl) {
      return;
    }

    // Clear stored state before navigation starts (IdP redirect unloads the page).
    localStorage.removeItem(StorageKeys.logoutState);

    try {
      await this.authenticationService.tryFederatedLogout({
        logoutUrl: this.idpLogoutUrl,
      });
    } catch {
      this.status = LogoutStatus.FAILED;
    }
  }

  public async goToLogin(): Promise<void> {
    // Clear logout state when navigating to login
    localStorage.removeItem(StorageKeys.logoutState);
    await this.router.navigate([AfRoutes.login]);
  }

  private getStoredLogoutState(): LogoutState | undefined {
    const rawState = localStorage.getItem(StorageKeys.logoutState);
    if (!rawState) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(rawState) as Partial<LogoutState>;
      if (!parsed || !parsed.status) {
        return undefined;
      }

      if (!Object.values(LogoutStatus).includes(parsed.status as LogoutStatus)) {
        return undefined;
      }

      return {
        status: parsed.status,
        ...(typeof parsed.idpLogoutUrl === 'string'
          ? { idpLogoutUrl: parsed.idpLogoutUrl }
          : {}),
      };
    } catch {
      return undefined;
    }
  }
}
